
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Pricing } from "@/components/Pricing";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";

const PlanSelection = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkExistingAccess = async () => {
      try {
        console.log('Checking for existing access...');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log('No session found, redirecting to auth');
          navigate('/auth', { replace: true });
          return;
        }

        // Check for active subscription or trial
        const [subscriptionResult, trialResult] = await Promise.all([
          supabase
            .from('user_subscriptions')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('free_trials')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('status', 'active')
            .maybeSingle()
        ]);

        const hasSubscription = !!subscriptionResult.data;
        const hasTrial = !!trialResult.data;

        if (hasSubscription || hasTrial) {
          console.log('Found active access, redirecting to dashboard');
          toast({
            title: "Active Access Found",
            description: "Redirecting to dashboard...",
            className: "bg-green-50 border-green-200",
          });
          navigate('/dashboard', { replace: true });
          return;
        }

        console.log('No active access found, showing plan selection');
        setIsChecking(false);

      } catch (error) {
        console.error('Error checking access:', error);
        toast({
          title: "Error Checking Access",
          description: "Please try again or contact support if the issue persists.",
          variant: "destructive",
        });
        setIsChecking(false);
      }
    };

    checkExistingAccess();
  }, [navigate, toast]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of CSO Agent.",
      });
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Error signing out",
        description: "There was a problem signing out. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <CSOLoadingSpinner size="lg" />
          <p className="text-gray-600">Checking your access status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-end py-4">
          <Button
            variant="ghost"
            onClick={handleSignOut}
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
        <Pricing />
      </div>
    </div>
  );
};

export default PlanSelection;
